import React from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import {
  FaBook,
  FaClock,
  FaChartLine,
  FaEdit,
  FaClipboardList,
  FaCheckCircle,
  FaLayerGroup,
  FaQuestionCircle,
} from "react-icons/fa";

import {
  useGetEnglishQuizByIdQuery,
  usePublishEnglishQuizMutation,
} from "../../slices/englishQuizApiSlice";

import {
  useGetEnglishQuizBlueprintByQuizQuery,
} from "../../slices/englishQuizBlueprintApiSlice";

const EnglishQuizDetails = () => {
  const { quizId } = useParams();
  const navigate = useNavigate();

  const {
    data: quizResponse,
    isLoading: isQuizLoading,
    error: quizError,
  } = useGetEnglishQuizByIdQuery(quizId, {
    skip: !quizId,
  });

  const quiz =
    quizResponse?.quiz ||
    quizResponse?.data ||
    quizResponse;

  const {
    data: blueprintResponse,
    isLoading: isBlueprintLoading,
  } = useGetEnglishQuizBlueprintByQuizQuery(quizId, {
    skip: !quizId,
  });

  const blueprint =
    blueprintResponse?.blueprint ||
    blueprintResponse?.data ||
    blueprintResponse;

  const [
    publishEnglishQuiz,
    { isLoading: isPublishing },
  ] = usePublishEnglishQuizMutation();

  const publishHandler = async () => {
    if (!blueprint) {
      toast.error(
        "Please create the quiz blueprint before publishing."
      );
      return;
    }

    if (
      !blueprint.sections ||
      blueprint.sections.length === 0
    ) {
      toast.error(
        "Blueprint must contain at least one section."
      );
      return;
    }

    const confirmPublish = window.confirm(
      "Are you sure you want to publish this English quiz?"
    );

    if (!confirmPublish) {
      return;
    }

    try {
      await publishEnglishQuiz(quizId).unwrap();

      toast.success(
        "English quiz published successfully."
      );
    } catch (err) {
      toast.error(
        err?.data?.message ||
          err?.error ||
          "Failed to publish quiz"
      );
    }
  };

  if (isQuizLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <p className="text-gray-600">
          Loading English quiz...
        </p>
      </div>
    );
  }

  if (quizError || !quiz) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <div className="w-full max-w-md rounded-2xl border border-red-200 bg-white p-6 text-center shadow">
          <h2 className="text-xl font-semibold text-red-700">
            English quiz not found
          </h2>

          <p className="mt-2 text-sm text-red-600">
            {quizError?.data?.message ||
              quizError?.error ||
              "Unable to load this quiz."}
          </p>

          <Link
            to="/englishquiz"
            className="mt-5 inline-flex rounded-lg bg-indigo-600 px-4 py-2 font-semibold text-white"
          >
            Back to English Quizzes
          </Link>
        </div>
      </div>
    );
  }

  // ======================================================
  // Get Subject ID
  // ======================================================

  const getSubjectId = () => {
    const subject =
      quiz.questionBankSubject ??
      quiz.subject ??
      quiz.questionBank?.subject;

    if (!subject) {
      return "";
    }

    if (typeof subject === "object") {
      return (
        subject._id ||
        subject.id ||
        subject.value ||
        ""
      );
    }

    return String(subject);
  };

  const subjectId = getSubjectId();

  console.log(
    "=== ENGLISH QUIZ DETAILS ==="
  );
  console.log("quizId:", quizId);
  console.log("quiz:", quiz);
  console.log("subjectId:", subjectId);
  console.log(
    "questionBankSubject:",
    quiz.questionBankSubject
  );

  const subject =
    typeof quiz.questionBankSubject === "object"
      ? quiz.questionBankSubject?.name
      : quiz.questionBankSubject ||
        (typeof quiz.subject === "object"
          ? quiz.subject?.name
          : quiz.subject) ||
        "English";

  const totalQuestions =
    quiz.totalQuestions ??
    blueprint?.sections?.reduce(
      (sum, section) =>
        sum +
        Number(section.numberOfQuestions || 0),
      0
    ) ??
    0;

  const totalMarks =
    quiz.totalMarks ??
    blueprint?.sections?.reduce(
      (sum, section) =>
        sum +
        Number(section.numberOfQuestions || 0) *
          Number(section.marksPerQuestion || 0),
      0
    ) ??
    0;

  const hasBlueprint =
    Boolean(blueprint) &&
    Array.isArray(blueprint.sections) &&
    blueprint.sections.length > 0;

  // ======================================================
  // IMPORTANT: Pass subjectId to Blueprint component
  // ======================================================

  const handleCreateBlueprint = () => {
    if (!subjectId) {
      toast.error(
        "Subject ID is missing from this English quiz."
      );

      console.error(
        "Cannot create blueprint: subjectId is missing.",
        quiz
      );

      return;
    }

    console.log(
      "Navigating to blueprint with:",
      {
        quizId,
        subjectId,
      }
    );

    navigate(
      `/english-quizzes/${quizId}/blueprint/create`,
      {
        state: {
          quizId,
          subjectId,
          quiz,
        },
      }
    );
  };

  const handleEditBlueprint = () => {
    navigate(
      `/englishquizBlueprint/${quizId}/edit`
    );
  };

  const handleEditQuiz = () => {
    navigate(`/englishquiz/${quizId}/edit`);
  };

  const handleGenerateQuestions = () => {
    navigate(`/englishquiz/${quizId}/generate`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 via-blue-50 to-indigo-100 px-4 py-8">
      <div className="mx-auto max-w-5xl">

        <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">

          <Link
            to="/englishquiz"
            className="w-fit rounded-xl bg-white px-5 py-3 text-blue-600 shadow"
          >
            ← Back to English Quizzes
          </Link>

          <div className="flex flex-wrap gap-3">

            {quiz.status !== "published" && (
              <button
                type="button"
                onClick={publishHandler}
                disabled={
                  isPublishing ||
                  isBlueprintLoading
                }
                className="inline-flex items-center gap-2 rounded-xl bg-green-700 px-5 py-3 font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
              >
                <FaCheckCircle />

                {isPublishing
                  ? "Publishing..."
                  : "Publish Quiz"}
              </button>
            )}

            <button
              type="button"
              onClick={handleEditQuiz}
              className="inline-flex items-center gap-2 rounded-xl bg-amber-500 px-5 py-3 font-semibold text-white"
            >
              <FaEdit />
              Edit Quiz
            </button>

            {!isBlueprintLoading &&
              (hasBlueprint ? (
                <button
                  type="button"
                  onClick={handleEditBlueprint}
                  className="inline-flex items-center gap-2 rounded-xl bg-purple-600 px-5 py-3 font-semibold text-white"
                >
                  <FaLayerGroup />
                  Edit Blueprint
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleCreateBlueprint}
                  className="inline-flex items-center gap-2 rounded-xl bg-purple-600 px-5 py-3 font-semibold text-white"
                >
                  <FaLayerGroup />
                  Create Blueprint
                </button>
              ))}

            <button
              type="button"
              onClick={handleGenerateQuestions}
              className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-5 py-3 font-semibold text-white"
            >
              <FaQuestionCircle />
              Generate Questions
            </button>
          </div>
        </div>

        <div className="overflow-hidden rounded-3xl bg-white shadow-2xl">

          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-8 text-white">

            <div className="flex items-center gap-4">

              <div className="rounded-2xl bg-white/20 p-4">
                <FaClipboardList className="text-4xl" />
              </div>

              <div className="min-w-0">

                <h1 className="text-3xl font-bold sm:text-4xl">
                  {quiz.name ||
                    "Untitled English Quiz"}
                </h1>

                <p className="mt-2 text-blue-100">
                  {quiz.description ||
                    "No description available"}
                </p>

              </div>
            </div>
          </div>

          <div className="grid gap-5 p-8 sm:grid-cols-2 lg:grid-cols-3">

            <DetailCard
              label="Subject"
              icon={
                <FaBook className="text-blue-600" />
              }
              value={subject}
            />

            <DetailCard
              label="Duration"
              icon={
                <FaClock className="text-green-600" />
              }
              value={
                quiz.durationMinutes != null
                  ? `${quiz.durationMinutes} minutes`
                  : "Not specified"
              }
            />

            <DetailCard
              label="Passing Percentage"
              icon={
                <FaChartLine className="text-purple-600" />
              }
              value={`${quiz.passingPercentage ?? 0}%`}
            />

            <DetailCard
              label="Questions"
              value={totalQuestions}
            />

            <DetailCard
              label="Total Marks"
              value={totalMarks}
            />

            <DetailCard
              label="Blueprint"
              icon={
                <FaLayerGroup className="text-indigo-600" />
              }
              value={
                isBlueprintLoading
                  ? "Loading..."
                  : hasBlueprint
                    ? `${blueprint.sections.length} Sections`
                    : "Not created"
              }
            />

          </div>

          <div className="border-t border-slate-200 px-8 py-6">

            <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">

              <div>
                <p className="text-sm font-semibold text-slate-500">
                  Status
                </p>

                <span
                  className={`mt-2 inline-flex rounded-full px-4 py-2 text-sm font-semibold ${
                    quiz.status === "published"
                      ? "bg-green-100 text-green-700"
                      : quiz.status === "archived"
                        ? "bg-gray-200 text-gray-700"
                        : "bg-yellow-100 text-yellow-700"
                  }`}
                >
                  {quiz.status?.toUpperCase() ||
                    "DRAFT"}
                </span>
              </div>

              {!isBlueprintLoading &&
                (hasBlueprint ? (
                  <button
                    type="button"
                    onClick={handleEditBlueprint}
                    className="inline-flex items-center justify-center gap-2 rounded-xl border border-purple-200 bg-purple-50 px-5 py-3 font-semibold text-purple-700 hover:bg-purple-100"
                  >
                    <FaLayerGroup />
                    Open Quiz Blueprint
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handleCreateBlueprint}
                    className="inline-flex items-center justify-center gap-2 rounded-xl bg-purple-600 px-5 py-3 font-semibold text-white hover:bg-purple-700"
                  >
                    <FaLayerGroup />
                    Create English Quiz Blueprint
                  </button>
                ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const DetailCard = ({
  label,
  value,
  icon,
}) => {
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-5">

      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
        {label}
      </p>

      <div className="mt-3 flex items-center gap-3 text-lg font-bold text-slate-900">
        {icon}

        <span>
          {value ?? "N/A"}
        </span>
      </div>
    </div>
  );
};

export default EnglishQuizDetails;